<template>
  <div class="research">
    <h1 v-bg-typography="'h1'">Research</h1>
    <div>
      <div class="search-filters">
        <bg-field
          class="search-input"
          style="margin-right: 25px;"
          label="Search"
          v-model="searchString"
          name="search"
        >
          <bg-input
            name="search"
            placeholder="Type in a test name/requester/company"
            v-model="searchString"
            height="46px"
          />
        </bg-field>
        <bg-field
          class="field"
          label="Platform"
          style="margin-right: 25px; max-width: 200px;"
        >
          <bg-select
            @change="selectDevice"
            :options="deviceOptions"
            :value="device"
            label="text"
          ></bg-select>
        </bg-field>
        <bg-field label="Status">
          <div class="filter-container">
            <bg-filter-bubble
              class="filter"
              v-for="filter in filters"
              :key="filter"
              :text="filter | BgKebabReadable"
              :variant="filter | BgKebab"
              :active="activeFilters.includes(filter)"
              @click.native="toggleFilter($event, filter)"
            />
          </div>
        </bg-field>
      </div>
    </div>
    <List
      ref="list"
      :headers="list.headers"
      :values="list.values"
      :actions="list.actions"
      :data="testList"
      :fractions="list.fractions"
      @change="getChanges"
      :pagination="true"
      :testSetups="testSetupsList"
      v-if="!$store.state.tests.loading"
    />
    <bg-loader :dark="true" v-if="$store.state.tests.loading"></bg-loader>
    <bg-modal
      v-if="isModalOpen"
      body="Are you sure you want to remove this test?"
      @close="isModalOpen = false"
    >
      <bg-button
        variant="secondary"
        text="Cancel"
        @click="isModalOpen = false"
      />
      <bg-button
        variant="destructive"
        class="action"
        text="Delete"
        @click="deleteTest"
      />
    </bg-modal>
  </div>
</template>

<style scoped>
.search-filters {
  display: flex;
}
.filter {
  margin-right: 12px;
}
.filter-container {
  display: flex;
  flex-direction: row;
  height: 46px;
  align-items: center;
}
.search-input {
  max-width: 300px;
  margin-right: 140px;
}
</style>

<script>
import BgNewList from "@/components/bg-new-list";
import BgTypography from "@/directives/bg-typography";
import BgModal from "@/components/bg-modal";
import BgButton from "@/components/bg-button";
import BgLoader from "@/components/bg-loader";
import BgInput from "@/components/bg-input";
import BgFilterBubble from "@/components/bg-filter-bubble";
import BgField from "@/components/bg-field";
import BgKebab from "@/filters/bg-kebab";
import BgKebabReadable from "@/filters/bg-kebab-readable";
import BgDayMonthYear from "@/filters/bg-day-month-year";
import moment from "moment";
import API from "@/api";
import { mapState } from "vuex";
import List from "@/components/list";
import BgSelect from "@/components/bg-select";

export default {
  name: "research",
  components: {
    BgModal,
    BgButton,
    BgLoader,
    BgFilterBubble,
    BgInput,
    BgField,
    BgSelect,
    List,
  },
  filters: {
    BgDayMonthYear,
    BgKebab,
    BgKebabReadable,
  },
  directives: {
    BgTypography,
  },
  watch: {
    searchString: function() {
      const child = this.$refs.list;
      child.paginationReset();
    },
    activeFilters: function() {
      const child = this.$refs.list;
      child.paginationReset();
    },
    device: function() {
      const child = this.$refs.list;
      child.paginationReset();
    },
  },
  computed: {
    ...mapState({
      tests: (state) => state.tests.tests,
    }),
    testList() {
      if (this.searchString !== "") {
        this.animate = false;
      }

      const search = this.searchString.toLowerCase();

      const filteredBySearch =
        this.searchString !== ""
          ? this.tests.filter((t) => {
              if (!t || typeof t !== "object") return false;
              const name = typeof t.name === "string" ? t.name.toLowerCase() : "";
              const company = t.company && typeof t.company.name === "string"
                ? t.company.name.toLowerCase()
                : "";
              const fullName =
                t.requester && t.requester.profile
                  ? `${t.requester.profile.first_name || ""} ${t.requester.profile.last_name || ""}`.toLowerCase()
                  : "";

              return (
                name.includes(search) ||
                fullName.includes(search) ||
                company.includes(search)
              );
            })
          : this.tests;

      if (this.activeFilters.length === 0 && this.device === "All") {
        return filteredBySearch;
      }

      return filteredBySearch.filter((d) => {
        let deviceCheck = true;
        let statusCheck = true;

        if (this.device !== "All") {
          deviceCheck = d.platform === this.device.toLowerCase();
        }

        if (this.activeFilters.length > 0) {
          statusCheck = this.activeFilters.includes(d.state);
        }

        return statusCheck && deviceCheck;
      });
    },

  },
  methods: {
    getChanges(val) {
      if (val.action === "Select") {
        this.$router.push(`/research/test/${val.item.id}/test-setup`);
      } else if (val.action === "Delete") {
        this.isModalOpen = true;
        this.deleteTestId = val.item.id;
      } else if (val.action === "View") {
        if (val.item.platform === "video") {
          this.$router.push(`/video-test/${val.item.id}/summary`);
        } else {
          this.$router.push(`/test/${val.item.id}/overview`);
        }
      } else if (val.action === "Start Test") {
        const ip = val.testSetup.ip;
        const defaultPort = 8080;

        // Parse the URL
        const url = new URL(`http://${ip}`);
        if (!url.port) {
            url.port = defaultPort;
        }

        const finalUrl = `${url.origin}/?testId=${val.item.id}`;
        window.open(
          finalUrl,
          "_blank"
        );
      }
    },
    toggleFilter(event, filter) {
      this.animate = false;
      if (this.activeFilters.includes(filter)) {
        this.activeFilters = this.activeFilters.filter((f) => f !== filter);
      } else {
        this.activeFilters.push(filter);
      }
    },
    handleActions() {
      // if (user.actionType === "delete") {
      //   this.isModalOpen = true;
      //   this.userToDelete = user.id;
      // }
    },
    async deleteTest() {
      this.isDeleting = true;
      const id = this.deleteTestId;
      API.test.deleteTest(id).then(() => {
        this.isDeleting = false;
        this.isModalOpen = false;
        const testIndex = this.tests.findIndex(
          (el) => el.id === this.deleteTestId
        );
        this.tests.splice(testIndex, 1);
      });
    },
    selectDevice(val) {
      this.device = val;
    },
  },
  data() {
    return {
      device: "All",
      deviceOptions: ["All", "Desktop", "Mobile", "Video"],
      isLoading: true,
      animate: true,
      isModalOpen: false,
      list: {
        headers: ["Company", "Title", "Status", "Platform", "Requester"],
        actions: ["Choose Setup", "View", "Delete"],
        values: [
          "company.name",
          "name",
          "state",
          "platform",
          "requester.email",
        ],
        fractions: [1, 2, 1, 1, 2],
      },
      searchString: "",
      activeFilters: [],
      filters: ["draft", "requested", "in-progress", "done"],
      headers: [
        { label: "Name", value: "name" },
        { label: "Type", value: "platform" },
        { label: "State", value: "state" },
        {
          label: "Company",
          value: "company",
          format: (value) => (value !== null ? value["name"] : ""),
        },
        {
          label: "Requester",
          value: "requester",
          format: (value) =>
            value !== null
              ? value["profile"]["first_name"] +
                " " +
                value["profile"]["last_name"]
              : "",
        },
        {
          label: "Last update",
          value: "updated_at",
          format: (value) =>
            value === 0 ? "" : moment.unix(value).format("DD/MM/YY"),
        },
      ],
      state: {
        draft: "Draft",
        requested: "Requested",
        "in-progress": "In progress",
        done: "Done",
      },
      deleteTestId: null,
      actions: [],
      //   {
      //     type: "delete",
      //     icon: "trashcan",
      //     tooltip: "Delete member"
      //   }
      // ]
      testSetupsList: [],
    };
  },
  mounted() {
    API.testSetups.getTestSetups().then((res) => {
      this.testSetupsList = res.data.test_setups;
    });
  },
};
</script>
